import React from 'react'
import { MODAL_ID } from '@/constants/modalId'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import Button from '@/components/Button'
import Field from '@/components/Field'
import { Input } from '@/components/Inputs'
import NiceModal from '@ebay/nice-modal-react'
import Modal, { hideModal } from '@/components/Modals/ModalV2'
import { SetNowButton, SetTimeButton } from './shared/settings'
import { Season } from '@/features/arcade/types'
import { useArcadeSettingsMutation } from '@/api/resources/arcade/season/set/delete_me'
import ControlledSelect from '@/components/Select/ControlledSelect'
import { useAllArcadeSeasonsQuery } from '@/api/resources/arcade/seasons/get'
import { SeasonsGetResponse } from '@/api/resources/arcade/season/get/schema'
import { useQueryClient } from '@tanstack/react-query'

type Props = {
  season: Season
}

type ArcadeSettingsInputs = {
  season_id: number
  startAt: string
  endAt: string
  rankThreshold: number
}

export const ArcadeSettings = NiceModal.create<Props>(({ season }: Props) => {
  const methods = useForm<ArcadeSettingsInputs>({
    defaultValues: {
      season_id: season?.id,
      startAt: season?.startAt,
      endAt: season?.endAt,
      rankThreshold: season?.rankThreshold
    }
  })
  const queryClient = useQueryClient()

  const { data: seasonsData } = useAllArcadeSeasonsQuery()
  const arcadeSettingsMutation = useArcadeSettingsMutation()

  const { handleSubmit } = methods

  if (!seasonsData) return

  const onSubmit = async data => {
    // perform the update to season
    await arcadeSettingsMutation.mutateAsync({
      season_id: !!data.season_id.value ? data.season_id.value : data.season_id,
      time_start: data.startAt,
      time_end: data.endAt,
      rank_threshold: data.rankThreshold
    })

    // Invalidate the arcade/season/active/get to refresh underlying page.
    queryClient.invalidateQueries({
      queryKey: ['/arcade/season/active/get']
    })
    queryClient.invalidateQueries({
      queryKey: ['/arcade/seasons/get', 'all']
    })

    hideModal(MODAL_ID.devOnly.arcadeSettings)
  }

  return (
    <Modal title={'Arcade Settings'} id={MODAL_ID.devOnly.arcadeSettings}>
      {({ ModalBody, ModalFooter }) => (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody className="space-y-2">
              <ArcadeSettingsForm season={season} seasons={seasonsData} />
            </ModalBody>
            <ModalFooter className="flex gap-2">
              <Button className="w-full" type="submit">
                Update
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      )}
    </Modal>
  )
})

type ArcadeSettingsFormProps = {
  season: Season
  seasons: SeasonsGetResponse
}

const ArcadeSettingsForm = ({ season, seasons }: ArcadeSettingsFormProps) => {
  const { register, control } = useFormContext()

  return (
    <>
      <Field className="w-full" name={'season_id'} label="Season ID">
        <ControlledSelect
          name={'season_id'}
          searchable={false}
          options={seasons?.data.map(season => ({
            label: season.id,
            value: season.id
          }))}
          formProps={{
            name: 'season_id',
            control
          }}
          selectorProps={{
            placeholder: 'Select From Available Season IDs'
          }}
        />
      </Field>
      <Field className="w-full" name={'startAt'} label="Start Time">
        <Input
          register={register('startAt')}
          id={'startAt'}
          placeholder="Start Time"
        />
        <div className="mt-1 flex justify-between gap-2">
          <div className="flex gap-2">
            <SetNowButton inputId="startAt" />
            <SetTimeButton time="10s" inputId="startAt" />
            <SetTimeButton time="30m" inputId="startAt" />
            <SetTimeButton time="8h" inputId="startAt" />
          </div>
          <ResetButton inputId="startAt" season={season} />
        </div>
      </Field>
      <Field className="w-full" name={'endAt'} label="End Time">
        <Input register={register('endAt')} placeholder="End Time" />
        <div className="mt-1 flex justify-between gap-2">
          <div className="flex gap-2">
            <SetNowButton inputId="endAt" />
            <SetTimeButton time="10s" inputId="endAt" />
            <SetTimeButton time="30m" inputId="endAt" />
            <SetTimeButton time="8h" inputId="endAt" />
          </div>
          <ResetButton inputId="endAt" season={season} />
        </div>
      </Field>
      <Field className="w-full" name={'rankThreshold'} label="Rank Threshhold">
        <Input
          register={register('rankThreshold')}
          placeholder="Rank Threshold"
          required={true}
          defaultValue={333}
        />
        <div className="mt-1 flex justify-end gap-2">
          <ResetButton inputId="rankThreshold" season={season} />
        </div>
      </Field>
    </>
  )
}

const ResetButton = ({ inputId, season }) => {
  const { setValue } = useFormContext()

  const resetInput = (input: keyof ArcadeSettingsInputs) => {
    if (!season[input]) return
    setValue(inputId, season[input].toString())
  }

  return (
    <Button
      theme="clean"
      size="sm"
      onClick={() => resetInput(inputId)}
      className="font-bold"
    >
      Reset
    </Button>
  )
}
